import { useState, useEffect } from "react"
import { Check, CircleAlert, Lock, Plus, Trash2 } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

// Dual-ring progress visual + status + inline edit.
// Props:
//   rock: { id, title, status, progressPct, milestones, updatedAt, createdAt, result }
//   quarterStartAt, quarterEndAt — ISO strings used for pacing math
//   readOnly: suppresses edit affordances
//   onUpdate(field, value): called on every accepted edit (parent owns PATCH)
//
// Pacing dial only renders when status === "on_track" AND progress is behind
// the expected linear pace by >= BEHIND_THRESHOLD_PCT. Status tag always wins
// the progress ring color.
const BEHIND_THRESHOLD_PCT = 10
const RING_SIZE = 120
const RING_STROKE = 10
const PACING_STROKE = 4

const STATUS_CONFIG = {
  on_track: { label: "On track", ringColor: "#c1272f", pill: "bg-kf-red/10 text-kf-red" },
  at_risk: { label: "At risk", ringColor: "#d97706", pill: "bg-amber-100 text-amber-800" },
  blocked: { label: "Blocked", ringColor: "#7f1d1d", pill: "bg-red-100 text-red-900" },
  done: { label: "Done", ringColor: "#15803d", pill: "bg-green-100 text-green-800" },
}

function computeExpectedPct({ rockCreatedAt, quarterStartAt, quarterEndAt, now = new Date() }) {
  const start = rockCreatedAt ? new Date(rockCreatedAt) : new Date(quarterStartAt)
  const end = new Date(quarterEndAt)
  const total = end - start
  if (total <= 0) return 0
  const elapsed = now - start
  return Math.max(0, Math.min(100, (elapsed / total) * 100))
}

function formatRelative(iso) {
  if (!iso) return "not updated yet"
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

function ProgressRing({ progressPct, expectedPct, ringColor, showPacing }) {
  const cx = RING_SIZE / 2
  const cy = RING_SIZE / 2
  const r = (RING_SIZE - RING_STROKE) / 2
  const rPacing = r + RING_STROKE / 2 + PACING_STROKE / 2 + 2
  const circumference = 2 * Math.PI * r
  const progressOffset = circumference - (Math.max(0, Math.min(100, progressPct)) / 100) * circumference

  // Pacing arc from progressPct → expectedPct (amber), only when behind
  const pacingCircumference = 2 * Math.PI * rPacing
  const pacingSpan = Math.max(0, expectedPct - progressPct) / 100
  const pacingDash = pacingCircumference * pacingSpan
  const pacingGap = pacingCircumference - pacingDash
  const pacingRotation = (progressPct / 100) * 360 - 90 // align to progress endpoint

  return (
    <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} className="block">
      {/* Rail */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e4e4e7" strokeWidth={RING_STROKE} />
      {/* Progress */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={ringColor}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={progressOffset}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: "stroke-dashoffset 300ms ease, stroke 200ms ease" }}
      />
      {/* Pacing amber arc (behind-pace overlay) */}
      {showPacing && pacingSpan > 0 && (
        <circle
          cx={cx}
          cy={cy}
          r={rPacing}
          fill="none"
          stroke="#d97706"
          strokeOpacity="0.6"
          strokeWidth={PACING_STROKE}
          strokeLinecap="round"
          strokeDasharray={`${pacingDash} ${pacingGap}`}
          transform={`rotate(${pacingRotation} ${cx} ${cy})`}
        />
      )}
      {/* Label */}
      <text
        x={cx}
        y={cy - 2}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-zinc-900 font-display"
        fontSize="26"
        fontWeight="700"
      >
        {Math.round(progressPct)}%
      </text>
      {showPacing && pacingSpan > 0 && (
        <text
          x={cx}
          y={cy + 18}
          textAnchor="middle"
          className="fill-amber-700"
          fontSize="10"
          fontWeight="500"
        >
          behind by {Math.round(expectedPct - progressPct)}%
        </text>
      )}
    </svg>
  )
}

function MilestoneEditor({ milestones, onChange, readOnly }) {
  const [newLabel, setNewLabel] = useState("")

  const toggle = (id) => {
    if (readOnly) return
    const next = milestones.map((m) => (m.id === id ? { ...m, done: !m.done } : m))
    onChange(next)
  }

  const add = () => {
    const label = newLabel.trim()
    if (!label || readOnly) return
    const next = [...milestones, { id: crypto.randomUUID(), label, done: false }]
    setNewLabel("")
    onChange(next)
  }

  const remove = (id) => {
    if (readOnly) return
    onChange(milestones.filter((m) => m.id !== id))
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-zinc-500 uppercase tracking-wide">Milestones</p>
      {milestones.length === 0 && (
        <p className="text-sm text-zinc-500 italic">No milestones yet.</p>
      )}
      <ul className="space-y-1.5">
        {milestones.map((m) => (
          <li key={m.id} className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => toggle(m.id)}
              disabled={readOnly}
              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                m.done
                  ? "bg-kf-red border-kf-red text-white"
                  : "bg-white border-zinc-300 hover:border-zinc-400"
              } ${readOnly ? "cursor-default" : "cursor-pointer"}`}
            >
              {m.done && <Check className="w-3 h-3" />}
            </button>
            <span className={`flex-1 ${m.done ? "line-through text-zinc-400" : "text-zinc-800"}`}>
              {m.label}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => remove(m.id)}
                className="text-zinc-400 hover:text-red-600"
                aria-label="Remove milestone"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && (
        <div className="flex gap-1.5 pt-1">
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder="Add a milestone…"
            className="h-8 text-sm"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={add}
            disabled={!newLabel.trim()}
            className="h-8 px-2"
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}

export function RockCard({
  rock,
  quarterStartAt,
  quarterEndAt,
  readOnly = false,
  onUpdate = () => {},
  onRemove,
  showFinalized = false,
}) {
  const [expanded, setExpanded] = useState(false)
  const [titleEdit, setTitleEdit] = useState(rock.title || "")
  useEffect(() => {
    setTitleEdit(rock.title || "")
  }, [rock.id, rock.title])

  const commitTitle = () => {
    if (readOnly) return
    const t = titleEdit.trim()
    if (!t) {
      setTitleEdit(rock.title || "")
      return
    }
    if (t !== (rock.title || "")) onUpdate("title", t)
  }

  const status = STATUS_CONFIG[rock.status] || STATUS_CONFIG.on_track
  const milestones = rock.milestones || []
  const hasMilestones = milestones.length > 0
  const progressPct = hasMilestones
    ? Math.round((milestones.filter((m) => m.done).length / milestones.length) * 100)
    : rock.progressPct || 0
  const expectedPct = computeExpectedPct({
    rockCreatedAt: rock.createdAt,
    quarterStartAt,
    quarterEndAt,
  })
  const isBehind = rock.status === "on_track" && progressPct < expectedPct - BEHIND_THRESHOLD_PCT
  const isFinalized = Boolean(rock.result)

  if (isFinalized && !showFinalized) return null

  const handleStatusChange = (next) => {
    if (readOnly) return
    onUpdate("status", next)
  }

  const handleProgressChange = (e) => {
    if (readOnly || hasMilestones) return
    onUpdate("progress_pct", Number(e.target.value))
  }

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-center">
        <ProgressRing
          progressPct={progressPct}
          expectedPct={expectedPct}
          ringColor={status.ringColor}
          showPacing={isBehind}
        />
      </div>

      <div className="flex flex-col items-center gap-1.5 text-center w-full">
        {!readOnly ? (
          <Input
            value={titleEdit}
            onChange={(e) => setTitleEdit(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), e.target.blur())}
            aria-label="Rock title"
            className="font-display text-lg text-center text-zinc-900 h-auto py-1 border-zinc-200 focus-visible:border-kf-red"
          />
        ) : (
          <h3 className="font-display text-lg text-zinc-900 leading-tight">{rock.title}</h3>
        )}
        <Popover>
          <PopoverTrigger asChild disabled={readOnly}>
            <button
              type="button"
              disabled={readOnly}
              className={`text-xs font-semibold uppercase tracking-wide rounded-full px-2.5 py-1 ${status.pill} ${
                readOnly ? "cursor-default" : "cursor-pointer hover:opacity-80"
              }`}
            >
              {status.label}
            </button>
          </PopoverTrigger>
          {!readOnly && (
            <PopoverContent className="w-44 p-1.5" align="center">
              <div className="flex flex-col gap-0.5">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleStatusChange(key)}
                    className={`text-sm text-left px-2 py-1.5 rounded hover:bg-zinc-100 flex items-center gap-2 ${
                      rock.status === key ? "font-semibold" : ""
                    }`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: cfg.ringColor }}
                    />
                    {cfg.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          )}
        </Popover>
        {rock.updatedAt || rock.createdAt ? (
          <p className="text-xs text-zinc-400">
            Updated {formatRelative(rock.updatedAt || rock.createdAt)}
          </p>
        ) : null}
      </div>

      {expanded && (
        <div className="border-t border-zinc-100 pt-3 space-y-3">
          {!hasMilestones && !readOnly && (
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">
                Progress
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={progressPct}
                onChange={handleProgressChange}
                className="w-full accent-kf-red"
              />
              <div className="flex justify-between text-[10px] text-zinc-400">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          )}
          {hasMilestones && !readOnly && (
            <div className="flex items-start gap-2 text-xs text-zinc-500 bg-zinc-50 rounded-md p-2">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Progress is computed from milestones below. Clear all milestones to use the slider directly.
              </span>
            </div>
          )}
          <MilestoneEditor
            milestones={milestones}
            onChange={(next) => onUpdate("milestones", next)}
            readOnly={readOnly}
          />
          {isBehind && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
              <CircleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                You're behind the linear pace by about {Math.round(expectedPct - progressPct)}%. If that's
                intentional or you're blocked, tag your status accordingly.
              </span>
            </div>
          )}
          {!readOnly && onRemove && (
            <div className="pt-2 border-t border-zinc-100">
              <button
                type="button"
                onClick={onRemove}
                className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-red-600"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete this rock
              </button>
            </div>
          )}
        </div>
      )}

      {!readOnly && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-zinc-500 hover:text-kf-red text-center pt-1 border-t border-zinc-100"
        >
          {expanded ? "Close" : "Update progress"}
        </button>
      )}
    </div>
  )
}

const MAX_QUARTER_ROCKS = 5

export function RockGrid({
  rocks = [],
  quarterStartAt,
  quarterEndAt,
  readOnly = false,
  onUpdate,
  onRemoveRock,
  showFinalized = false,
  onAddRock,
  addRockBusy = false,
  maxRocks = MAX_QUARTER_ROCKS,
}) {
  // Default behavior (showFinalized=false) hides finalized rocks so the
  // active dashboard stays focused on the in-flight set. History views
  // pass showFinalized=true so coaches can see what the rocks landed at
  // (met / partially_met / missed).
  const visible = showFinalized
    ? rocks.filter((r) => r.result)
    : rocks.filter((r) => !r.result)

  const canAddRock =
    Boolean(onAddRock) &&
    !readOnly &&
    !showFinalized &&
    rocks.length < maxRocks

  if (visible.length === 0) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-dashed border-zinc-300 p-8 text-center">
          <p className="text-zinc-500">
            {showFinalized
              ? "No finalized rocks for this quarter."
              : "No active rocks for this quarter."}
          </p>
          {canAddRock && (
            <Button
              type="button"
              variant="kf-outline"
              size="sm"
              className="mt-4 font-bold uppercase tracking-wider"
              onClick={() => onAddRock()}
              disabled={addRockBusy}
            >
              <Plus className="w-4 h-4 mr-1.5" aria-hidden />
              {addRockBusy ? "Adding…" : "Add a rock"}
            </Button>
          )}
        </div>
      </div>
    )
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((rock) => (
          <RockCard
            key={rock.id}
            rock={rock}
            quarterStartAt={quarterStartAt}
            quarterEndAt={quarterEndAt}
            readOnly={readOnly}
            onUpdate={(field, value) => onUpdate && onUpdate(rock.id, field, value)}
            onRemove={onRemoveRock ? () => onRemoveRock(rock) : undefined}
            showFinalized={showFinalized}
          />
        ))}
      </div>
      {canAddRock && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="kf-outline"
            size="sm"
            className="font-bold uppercase tracking-wider"
            onClick={() => onAddRock()}
            disabled={addRockBusy}
          >
            <Plus className="w-4 h-4 mr-1.5" aria-hidden />
            {addRockBusy ? "Adding…" : "Add another rock"}
          </Button>
        </div>
      )}
    </div>
  )
}
